
import logger from '@/utils/logger';
import { getCachedUIPreferences, cacheUIPreferences } from '@/utils/uiPreferences.storage';

class UIModeService {
  constructor() {
    this.DEFAULT_MODE = 'compact';
    this.AVAILABLE_MODES = ['default', 'compact'];
    this.EVENT_NAME = 'uiModeChanged';
  }

  /**
   * Get the current UI mode from saved preferences
   * @returns {string} The current UI mode
   */
  getCurrentMode() {
    try {
      return getCachedUIPreferences().uiMode;
    } catch (error) {
      logger.error('Error getting UI mode:', error);
      return this.DEFAULT_MODE;
    }
  }

  /**
   * Set and apply a UI mode
   * @param {string} mode - The mode to set ('default' or 'compact')
   * @param {boolean} saveToStorage - Whether to update cached preferences (default: true)
   * @returns {boolean} Success status
   */
  setMode(mode, saveToStorage = true) {
    try {
      // Validate mode
      if (!this.AVAILABLE_MODES.includes(mode)) {
        logger.warn(`Invalid UI mode: ${mode}. Using default.`);
        mode = this.DEFAULT_MODE;
      }

      if (saveToStorage) {
        const preferences = getCachedUIPreferences();
        cacheUIPreferences({ ...preferences, uiMode: mode });
      }

      // Apply the mode to DOM
      this.applyMode(mode);

      // Dispatch event for other components
      window.dispatchEvent(new CustomEvent(this.EVENT_NAME, {
        detail: { mode }
      }));

      return true;
    } catch (error) {
      logger.error('Error setting UI mode:', error);
      return false;
    }
  }

  /**
   * Toggle between available modes
   * @returns {string} The new mode after toggling
   */
  toggleMode() {
    const currentMode = this.getCurrentMode();
    const currentIndex = this.AVAILABLE_MODES.indexOf(currentMode);
    const nextIndex = (currentIndex + 1) % this.AVAILABLE_MODES.length;
    const newMode = this.AVAILABLE_MODES[nextIndex];

    this.setMode(newMode);
    return newMode;
  }

  /**
   * Apply a UI mode to the DOM
   * @param {string} mode - The mode to apply
   */
  applyMode(mode) {
    try {
      const body = document.body;
      const app = document.getElementById('app');

      // Remove all mode attributes
      body.removeAttribute('data-ui-mode');
      if (app) app.removeAttribute('data-ui-mode');

      // Always apply the mode attribute (required for CSS to work)
      // DEFAULT_MODE is just for fallback when nothing is saved, not for styling
      if (mode && this.AVAILABLE_MODES.includes(mode)) {
        body.setAttribute('data-ui-mode', mode);
        if (app) app.setAttribute('data-ui-mode', mode);
      }
    } catch (error) {
      logger.error('Error applying UI mode:', error);
    }
  }

  /**
   * Apply the current saved mode (used on app startup)
   */
  applyCurrentMode() {
    const currentMode = this.getCurrentMode();
    this.applyMode(currentMode);
  }

  /**
   * Get available UI modes configuration
   * @returns {Array} Array of mode configuration objects
   */
  getAvailableModes() {
    return [
      {
        value: 'default',
        label: 'Default',
        description: 'Standard spacing and font sizes. The original application design.',
        icon: 'bi bi-circle'
      },
      {
        value: 'compact',
        label: 'Compact',
        description: 'Tighter spacing and smaller fonts. Maximize information density.',
        icon: 'bi bi-arrows-collapse'
      }
    ];
  }

  /**
   * Get preview styles for a mode
   * @param {string} mode - The mode to get preview styles for
   * @returns {Object} Style object for preview
   */
  getPreviewStyle(mode) {
    const styles = {
      default: {
        padding: '1rem',
        fontSize: '0.875rem',
        gap: '0.75rem'
      },
      compact: {
        padding: '0.5rem',
        fontSize: '0.75rem',
        gap: '0.5rem'
      }
    };
    return styles[mode] || styles[this.DEFAULT_MODE];
  }

  /**
   * Reset to default mode
   */
  resetToDefault() {
    this.setMode(this.DEFAULT_MODE);
  }

  /**
   * Check if a mode is valid
   * @param {string} mode - The mode to validate
   * @returns {boolean} Whether the mode is valid
   */
  isValidMode(mode) {
    return this.AVAILABLE_MODES.includes(mode);
  }

  /**
   * Get mode label for display
   * @param {string} mode - The mode to get label for
   * @returns {string} The label for the mode
   */
  getModeLabel(mode) {
    const modes = this.getAvailableModes();
    const modeConfig = modes.find(m => m.value === mode);
    return modeConfig ? modeConfig.label : mode;
  }
}

// Export singleton instance
export default new UIModeService();
